import styled from "@emotion/styled";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Badge from "react-bootstrap/Badge";

import BackendUserAPI from "../../api/backend/user";
import OverlayLoading from "../../components/forms/OverlayLoading";
import BreadcrumbOne from "../../components/forms/BreadcrumbOne";
import LaravelPagination from "../../components/forms/LaravelPagination";
import RoleSelect from "../../components/forms/RoleSelect";
import dayjs from "dayjs";

const context = createContext();

const RedSwitch = styled.div`
    .form-switch .form-check-input {
        height: 1.2rem;
        width: 2.2rem;
    }
    .form-check-input:checked {
        border-color: var(--bs-danger);
        background-color: var(--bs-danger);
    }
`;

const BackendUserPage = () => {
    const fetchParams = useRef({});

    const [data, setData] = useState({});
    const [paging, setPaging] = useState({ take: 10 });
    const [filter, setFilter] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchList = useCallback((payload) => {
        setLoading(() => true);
        BackendUserAPI.list(payload)
            .then((r) => setData(() => r.data))
            .finally(() => setLoading(() => false));
    }, []);

    const state = useMemo(() => {
        return {
            ...data,
            onChangePage: (value) => {
                setPaging((original) => ({ ...original, page: value }));
            },
            onChangeFilter: (values) => {
                setFilter((original) => ({ ...original, ...values }));
            },
            onChangeUserProperty: (id, changes) => {
                setLoading(() => true);
                BackendUserAPI.update(id, changes).finally(() => fetchList(fetchParams.current));
            },
        };
    }, [fetchList, data]);

    useEffect(() => {
        fetchParams.current = { ...paging, filter: filter };
        fetchList(fetchParams.current);
    }, [fetchList, paging, filter]);

    return (
        <context.Provider value={state}>
            <div className="position-relative">
                <BreadcrumbOne name="จัดการผู้ใช้" />

                <div className="mb-4">
                    <Header />
                </div>

                <div>
                    <Content />
                </div>

                <div>
                    <Footer />
                </div>

                {loading && <OverlayLoading />}
            </div>
        </context.Provider>
    );
};

const Header = () => {
    const mounted = useRef(false);
    const watchTimeout = useRef(null);

    const local = useContext(context);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (mounted.current) {
            clearTimeout(watchTimeout.current);
            watchTimeout.current = setTimeout(() => {
                local.onChangeFilter({ search });
            }, 300);
        }
    }, [search]);

    useEffect(() => {
        mounted.current = true;
    }, []);

    return (
        <Row>
            <Col>
                <Row>
                    <Col sm={6} lg={4}>
                        <Form.Group>
                            <Form.Label>Search</Form.Label>
                            <Form.Control type="text" value={search} onChange={(e) => setSearch(e.target.value.trim())} />
                        </Form.Group>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

const Content = () => {
    const local = useContext(context);
    const data = local?.data ?? [];

    const onChangeRole = (row, changes) => {
        local.onChangeUserProperty?.(row.id, { role: changes });
    };

    const onChangeSuspendedAt = (row, changes) => {
        local.onChangeUserProperty?.(row.id, { suspended_at: changes });
    };

    return (
        <Table responsive striped bordered hover className="mb-0">
            <thead>
                <tr>
                    <th>#</th>
                    <th style={{ width: "100%" }}>ชื่อ</th>
                    <th style={{ minWidth: 300, maxWidth: 300 }}>อีเมล</th>
                    <th style={{ minWidth: 300, maxWidth: 300 }}>หน่วยงาน</th>
                    <th className="text-center" style={{ minWidth: 125, maxWidth: 125 }}>
                        สิทธิ์
                    </th>
                    <th className="text-center" style={{ minWidth: 100, maxWidth: 100 }}>
                        ระงับ
                    </th>
                    <th className="text-center" style={{ width: 100, minWidth: 100, maxWidth: 100 }}>
                        ยืนยันอีเมล
                    </th>
                    <th style={{ width: 200, minWidth: 200, maxWidth: 200 }}>ลงทะเบียนเมื่อ</th>
                </tr>
            </thead>
            <tbody>
                {data.map((v, i) => (
                    <tr key={i}>
                        <td>{v.id}</td>
                        <td>{v.name}</td>
                        <td>{v.email}</td>
                        <td>{v.department ?? '-'}</td>
                        <td className="text-center">
                            <RoleSelect size="sm" value={v.role} onChange={(e) => onChangeRole(v, e.target.value)} />
                        </td>
                        <td className="text-center">
                            <RedSwitch>
                                <Form.Check
                                    checked={Boolean(v.suspended_at)}
                                    onChange={(e) => onChangeSuspendedAt(v, !v.suspended_at)}
                                    type="switch"
                                />
                            </RedSwitch>
                        </td>
                        <td className="text-center">{v.email_verified_at ? <Badge bg="success">YES</Badge> : <Badge bg="secondary">NO</Badge>}</td>
                        <td className="font-monospace">{v.created_at && dayjs(v.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const Footer = () => {
    const local = useContext(context);

    return (
        <div className="d-flex align-items-center flex-wrap mt-3">
            <div>ทั้งหมด {local.total ?? 0} รายการ</div>
            <div className="flex-fill"></div>
            <LaravelPagination {...local} />
        </div>
    );
};

export default BackendUserPage;
